import React, { useEffect, useRef, useState } from 'react';
import { Tab, TabsContainer } from './SectionTabs.styles';
import { useTranslation } from 'react-i18next';
import { isUK, isUkraine } from 'utils/countryCode';
import {
  InputAndDiagnosticsTabName,
  RecommendationTabName,
} from 'utils/constants/RecommendationSections';
import { useBreakpoint } from 'hooks';

const DEFAULT_HEADER_HEIGHT = 75;

export type SectionTab = 'Recommendation' | 'InputAndDiagnostics';

interface SectionTabsProps {
  selectedTab: SectionTab;
  handleTabClick: (tab: SectionTab) => void;
  isDDG?: boolean;
  countryCode?: string;
  isPreBookButtonVisible?: boolean;
}

const SectionTabs: React.FC<SectionTabsProps> = ({
  selectedTab,
  handleTabClick,
  isDDG,
  countryCode,
  isPreBookButtonVisible,
}) => {
  const isTabsSticky = !isDDG || isUkraine(countryCode) || isUK(countryCode);
  const [t] = useTranslation();
  const { isMobile } = useBreakpoint();
  const [topPosition, setTopPosition] = useState(DEFAULT_HEADER_HEIGHT);
  const observerRef = useRef<MutationObserver | null>(null);
  const resizeObserverRef = useRef<ResizeObserver | null>(null);
  const bannerFoundRef = useRef<boolean>(false);

  useEffect(() => {
    const calculateTopPosition = () => {
      const banner = document.querySelector('[data-banner="osr-edit"]') as HTMLElement | null;
      const header = document.querySelector('header') as HTMLElement | null;
      const measurableHeaderHeight = header?.offsetHeight ?? DEFAULT_HEADER_HEIGHT;
      const fallbackHeaderHeight = banner ? measurableHeaderHeight : DEFAULT_HEADER_HEIGHT;

      // Calculate prebook button offset
      const prebookOffset = isPreBookButtonVisible && isMobile ? 65 : 0;

      if (banner) {
        const calculatedTop = measurableHeaderHeight + banner.offsetHeight + prebookOffset;
        setTopPosition(calculatedTop);
        bannerFoundRef.current = true;

        if (observerRef.current) {
          observerRef.current.disconnect();
          observerRef.current = null;
        }

        if (!resizeObserverRef.current) {
          resizeObserverRef.current = new ResizeObserver(() => {
            const updatedHeader = document.querySelector('header') as HTMLElement | null;
            const updatedBanner = document.querySelector(
              '[data-banner="osr-edit"]'
            ) as HTMLElement | null;
            const updatedHeaderHeight = updatedHeader?.offsetHeight ?? DEFAULT_HEADER_HEIGHT;
            const updatedBannerHeight = updatedBanner?.offsetHeight ?? 0;
            // ADD THIS: Include prebook offset in resize calculation
            const currentPrebookOffset = isPreBookButtonVisible && isMobile ? 65 : 0;
            setTopPosition(updatedHeaderHeight + updatedBannerHeight + currentPrebookOffset);
          });
          resizeObserverRef.current.observe(banner);
          if (header) {
            resizeObserverRef.current.observe(header);
          }
        }

        return true;
      }

      setTopPosition(fallbackHeaderHeight + prebookOffset);
      return false;
    };

    calculateTopPosition();
  }, [isPreBookButtonVisible, isMobile]);

  return (
    <TabsContainer
      isTabsSticky={isTabsSticky}
      style={{ '--tabs-top': `${topPosition}px` } as React.CSSProperties}
    >
      <Tab
        className={selectedTab === RecommendationTabName ? 'selected' : ''}
        onClick={() => handleTabClick(RecommendationTabName)}
        data-testid="recommendation-tab"
      >
        {t('Recommendation Tab')}
      </Tab>
      <Tab
        className={selectedTab === InputAndDiagnosticsTabName ? 'selected' : ''}
        onClick={() => handleTabClick(InputAndDiagnosticsTabName)}
        data-testid="input-and-diagnostics-tab"
      >
        {t('Diagnostics')}
      </Tab>
    </TabsContainer>
  );
};

export default SectionTabs;
